/**
 * Tradescript — Simple strategy scripting DSL for MarketView
 *
 * Syntax:
 *   // comments
 *   BUY WHEN <condition>
 *   SELL WHEN <condition>
 *   SET <variable> = <expression>
 *
 * Conditions:
 *   CLOSE > SMA(14)
 *   RSI(14) < 30
 *   EMA(9) CROSSES_ABOVE EMA(21)
 *   MACD_SIGNAL(12,26,9) CROSSES_BELOW 0
 *   VOLUME > 1000000
 *
 * Built-in functions:
 *   OPEN, HIGH, LOW, CLOSE, VOLUME
 *   SMA(period), EMA(period)
 *   RSI(period)
 *   MACD(fast,slow,signal) — returns MACD line value
 *   MACD_SIGNAL(fast,slow,signal) — returns signal line value
 *   MACD_HIST(fast,slow,signal) — returns histogram value
 *   BOLLINGER_UPPER(period,stddev), BOLLINGER_LOWER(period,stddev)
 *   ATR(period)
 *   PREV(expression, barsBack) — value N bars ago
 */

export interface StrategyScript {
  buyCondition: ConditionNode | null;
  sellCondition: ConditionNode | null;
  variables: Map<string, ExpressionNode>;
  errors: ParseError[];
}

export interface ParseError {
  line: number;
  message: string;
}

export type ConditionNode =
  | { type: "comparison"; left: ExpressionNode; op: ComparisonOp; right: ExpressionNode }
  | { type: "crosses"; left: ExpressionNode; direction: "above" | "below"; right: ExpressionNode }
  | { type: "and"; left: ConditionNode; right: ConditionNode }
  | { type: "or"; left: ConditionNode; right: ConditionNode };

export type ComparisonOp = ">" | "<" | ">=" | "<=" | "==" | "!=";

export type ExpressionNode =
  | { type: "number"; value: number }
  | { type: "field"; name: "open" | "high" | "low" | "close" | "volume" }
  | { type: "function"; name: string; args: number[] }
  | { type: "variable"; name: string }
  | { type: "arithmetic"; left: ExpressionNode; op: "+" | "-" | "*" | "/"; right: ExpressionNode }
  | { type: "prev"; expr: ExpressionNode; barsBack: number };

const FIELDS = ["OPEN", "HIGH", "LOW", "CLOSE", "VOLUME"];
const FUNCTIONS = ["SMA", "EMA", "RSI", "MACD", "MACD_SIGNAL", "MACD_HIST", "BOLLINGER_UPPER", "BOLLINGER_LOWER", "ATR", "STOCH_K", "STOCH_D"];

export function parseStrategy(code: string): StrategyScript {
  const result: StrategyScript = {
    buyCondition: null,
    sellCondition: null,
    variables: new Map(),
    errors: [],
  };

  const lines = code.split("\n");

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i].trim();
    if (!raw || raw.startsWith("//")) continue;

    const lineNum = i + 1;
    const upper = raw.toUpperCase();

    try {
      if (upper.startsWith("BUY WHEN ")) {
        const condStr = raw.slice(9).trim();
        result.buyCondition = parseCondition(condStr);
      } else if (upper.startsWith("SELL WHEN ")) {
        const condStr = raw.slice(10).trim();
        result.sellCondition = parseCondition(condStr);
      } else if (upper.startsWith("SET ")) {
        const rest = raw.slice(4).trim();
        const eqIdx = rest.indexOf("=");
        if (eqIdx < 0) {
          result.errors.push({ line: lineNum, message: "SET requires = assignment" });
          continue;
        }
        const varName = rest.slice(0, eqIdx).trim().toUpperCase();
        const exprStr = rest.slice(eqIdx + 1).trim();
        result.variables.set(varName, parseExpression(exprStr));
      } else {
        result.errors.push({ line: lineNum, message: `Unknown statement: ${raw.split(" ")[0]}` });
      }
    } catch (e: any) {
      result.errors.push({ line: lineNum, message: e.message || "Parse error" });
    }
  }

  return result;
}

function parseCondition(str: string): ConditionNode {
  const upper = str.toUpperCase();

  // Handle AND/OR (lowest precedence)
  const andIdx = findTopLevelKeyword(upper, " AND ");
  if (andIdx >= 0) {
    return {
      type: "and",
      left: parseCondition(str.slice(0, andIdx).trim()),
      right: parseCondition(str.slice(andIdx + 5).trim()),
    };
  }

  const orIdx = findTopLevelKeyword(upper, " OR ");
  if (orIdx >= 0) {
    return {
      type: "or",
      left: parseCondition(str.slice(0, orIdx).trim()),
      right: parseCondition(str.slice(orIdx + 4).trim()),
    };
  }

  // Handle CROSSES_ABOVE / CROSSES_BELOW
  const crossAbove = findTopLevelKeyword(upper, " CROSSES_ABOVE ");
  if (crossAbove >= 0) {
    return {
      type: "crosses",
      left: parseExpression(str.slice(0, crossAbove).trim()),
      direction: "above",
      right: parseExpression(str.slice(crossAbove + 15).trim()),
    };
  }

  const crossBelow = findTopLevelKeyword(upper, " CROSSES_BELOW ");
  if (crossBelow >= 0) {
    return {
      type: "crosses",
      left: parseExpression(str.slice(0, crossBelow).trim()),
      direction: "below",
      right: parseExpression(str.slice(crossBelow + 15).trim()),
    };
  }

  // Handle comparison operators
  for (const op of [">=", "<=", "!=", "==", ">", "<"] as ComparisonOp[]) {
    const idx = findTopLevelOp(str, op);
    if (idx >= 0) {
      return {
        type: "comparison",
        left: parseExpression(str.slice(0, idx).trim()),
        op,
        right: parseExpression(str.slice(idx + op.length).trim()),
      };
    }
  }

  throw new Error(`Cannot parse condition: ${str}`);
}

function parseExpression(str: string): ExpressionNode {
  str = str.trim();
  const upper = str.toUpperCase();

  // Handle arithmetic (+ - at top level, then * /)
  for (const op of ["+", "-"] as const) {
    const idx = findTopLevelOp(str, op);
    if (idx > 0) {
      return {
        type: "arithmetic",
        left: parseExpression(str.slice(0, idx)),
        op,
        right: parseExpression(str.slice(idx + 1)),
      };
    }
  }
  for (const op of ["*", "/"] as const) {
    const idx = findTopLevelOp(str, op);
    if (idx > 0) {
      return {
        type: "arithmetic",
        left: parseExpression(str.slice(0, idx)),
        op,
        right: parseExpression(str.slice(idx + 1)),
      };
    }
  }

  // Parenthesized expression
  if (str.startsWith("(") && str.endsWith(")")) {
    return parseExpression(str.slice(1, -1));
  }

  // Number literal
  const num = parseFloat(str);
  if (!isNaN(num) && str === String(num)) {
    return { type: "number", value: num };
  }

  // PREV(expr, barsBack)
  if (upper.startsWith("PREV(") && str.endsWith(")")) {
    const inner = str.slice(5, -1);
    const lastComma = inner.lastIndexOf(",");
    if (lastComma >= 0) {
      const exprPart = inner.slice(0, lastComma).trim();
      const barsPart = inner.slice(lastComma + 1).trim();
      return {
        type: "prev",
        expr: parseExpression(exprPart),
        barsBack: parseInt(barsPart) || 1,
      };
    }
  }

  // Built-in field
  if (FIELDS.includes(upper)) {
    return { type: "field", name: upper.toLowerCase() as any };
  }

  // Function call: NAME(arg1, arg2, ...)
  const parenIdx = str.indexOf("(");
  if (parenIdx > 0 && str.endsWith(")")) {
    const funcName = str.slice(0, parenIdx).trim().toUpperCase();
    if (FUNCTIONS.includes(funcName) || funcName.startsWith("$")) {
      const argsStr = str.slice(parenIdx + 1, -1);
      const args = argsStr.split(",").map(a => parseFloat(a.trim())).filter(a => !isNaN(a));
      return { type: "function", name: funcName, args };
    }
  }

  // Variable reference
  if (/^[A-Z_][A-Z0-9_]*$/i.test(str)) {
    return { type: "variable", name: upper };
  }

  // Try as number (for things like "100.50")
  const numFallback = parseFloat(str);
  if (!isNaN(numFallback)) {
    return { type: "number", value: numFallback };
  }

  throw new Error(`Cannot parse expression: ${str}`);
}

/** Find keyword at "top level" (not inside parentheses) */
function findTopLevelKeyword(str: string, keyword: string): number {
  let depth = 0;
  for (let i = 0; i < str.length; i++) {
    if (str[i] === "(") depth++;
    if (str[i] === ")") depth--;
    if (depth === 0 && str.slice(i, i + keyword.length) === keyword) {
      return i;
    }
  }
  return -1;
}

function findTopLevelOp(str: string, op: string): number {
  let depth = 0;
  // Search right-to-left for left-associativity
  for (let i = str.length - 1; i >= 0; i--) {
    if (str[i] === ")") depth++;
    if (str[i] === "(") depth--;
    if (depth === 0 && str.slice(i, i + op.length) === op) {
      // Make sure it's not part of >= or <= for single > or <
      if ((op === ">" || op === "<") && (str[i - 1] === "!" || str[i + 1] === "=")) continue;
      return i;
    }
  }
  return -1;
}

/** Syntax keywords for highlighting */
export const TRADESCRIPT_KEYWORDS = ["BUY", "SELL", "WHEN", "SET", "AND", "OR", "CROSSES_ABOVE", "CROSSES_BELOW", "PREV"];
export const TRADESCRIPT_FUNCTIONS = FUNCTIONS;
export const TRADESCRIPT_FIELDS = FIELDS;

/** Apply syntax highlighting to tradescript code (returns HTML) */
export function highlightTradescript(code: string): string {
  return code
    .split("\n")
    .map(line => {
      if (line.trim().startsWith("//")) {
        return `<span class="ts-comment">${escapeHtml(line)}</span>`;
      }
      let result = escapeHtml(line);
      // Keywords
      for (const kw of TRADESCRIPT_KEYWORDS) {
        result = result.replace(
          new RegExp(`\\b${kw}\\b`, "gi"),
          `<span class="ts-keyword">${kw}</span>`
        );
      }
      // Functions  
      for (const fn of TRADESCRIPT_FUNCTIONS) {
        result = result.replace(
          new RegExp(`\\b${fn}\\b`, "gi"),
          `<span class="ts-function">${fn}</span>`
        );
      }
      // Fields
      for (const f of TRADESCRIPT_FIELDS) {
        result = result.replace(
          new RegExp(`\\b${f}\\b`, "gi"),
          `<span class="ts-field">${f}</span>`
        );
      }
      // Numbers
      result = result.replace(
        /\b(\d+\.?\d*)\b/g,
        '<span class="ts-number">$1</span>'
      );
      // Operators
      result = result.replace(
        /(&gt;=|&lt;=|!=|==|&gt;|&lt;)/g,
        '<span class="ts-operator">$1</span>'
      );
      return result;
    })
    .join("\n");
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Default strategy templates
 */
export const STRATEGY_TEMPLATES: Record<string, string> = {
  "Golden Cross": `// Golden Cross Strategy
// Buy when fast EMA crosses above slow EMA
// Sell when fast EMA crosses below slow EMA

BUY WHEN EMA(9) CROSSES_ABOVE EMA(21)
SELL WHEN EMA(9) CROSSES_BELOW EMA(21)`,

  "RSI Mean Reversion": `// RSI Mean Reversion
// Buy when RSI is oversold
// Sell when RSI is overbought

BUY WHEN RSI(14) < 30
SELL WHEN RSI(14) > 70`,

  "MACD Signal Cross": `// MACD Signal Line Crossover
// Buy when MACD crosses above signal
// Sell when MACD crosses below signal

BUY WHEN MACD(12,26,9) CROSSES_ABOVE MACD_SIGNAL(12,26,9)
SELL WHEN MACD(12,26,9) CROSSES_BELOW MACD_SIGNAL(12,26,9)`,

  "Bollinger Breakout": `// Bollinger Band Breakout
// Buy when price breaks above upper band
// Sell when price breaks below lower band

BUY WHEN CLOSE > BOLLINGER_UPPER(20,2)
SELL WHEN CLOSE < BOLLINGER_LOWER(20,2)`,

  "Price Momentum": `// Simple Price Momentum
// Buy when close is above SMA and volume confirms
// Sell when close drops below SMA

BUY WHEN CLOSE > SMA(20) AND VOLUME > 500000
SELL WHEN CLOSE < SMA(20)`,
};
